import type { LocationContent } from "./types";

export const chicago_seo: LocationContent = {
  "hero": "Chicago law firms need search visibility built around the matters attorneys actually accept, not a scattered list of disconnected terms. We review office details, practice pages and the full path a caller takes toward a scheduled consultation. Injury, family and criminal defense inquiries each deserve a distinct route into intake, and Chicago partners can track which searches turn into real consultations.",
  "whyHeading": "Attorney SEO in Chicago",
  "why": [
    "A Chicago firm with several practice groups needs search work that respects how each group actually operates. We start by asking which attorneys handle which matters and who answers the phone when a new caller reaches out. A broken contact form can matter more than a new blog post. Office hours and service descriptions deserve the same attention as any published article.",
    "Your attorneys decide how services are described publicly, and we keep that approval step visible rather than letting pages drift between departments. The same review applies to consultation details and contact routes across every practice group. Monthly summaries connect finished tasks with calls and submitted forms. Intake records then show which inquiries became consultations, helping owners weigh new work against current staff capacity."
  ],
  "marketHeading": "The Chicago Legal Search Market",
  "market": [
    "Cook County includes Evanston, Naperville, Schaumburg and Oak Brook, each raising its own question about where clients actually meet with attorneys in person for a scheduled consultation. A firm can serve residents from these nearby communities without running a separate office in each one. We ask where consultations actually happen before describing any wider coverage, weighing competition against services your attorneys provide rather than a simple list of neighboring names.",
    "Google Maps local pack results weigh a searcher's location alongside listing categories, reviews and overall prominence. So results seen near one office can differ noticeably from results seen farther away across the wider metro area. Accurate contact details and genuine client reviews help a profile reflect the practice that actually exists today. Google AI Overviews and ChatGPT may also summarize attorney information before a visit, and we compare those public summaries with approved firm facts."
  ],
  "marketLinks": [
    {
      "name": "Illinois",
      "url": "/lawyer-seo/illinois-seo"
    }
  ],
  "keywordHeading": "Searches Chicago clients use to find a lawyer",
  "keywordIntro": "People searching for Chicago attorneys often name the legal matter before the firm, so service pages need clear next steps.",
  "keywords": [
    "personal injury lawyers serving nearby offices",
    "car accident attorneys taking new cases",
    "family law attorneys handling local matters",
    "divorce lawyers accepting consultations nearby",
    "criminal defense attorneys available for calls"
  ],
  "includedHeading": "What's included in a Chicago engagement",
  "checklist": [
    "Audit Chicago office profiles for accurate contact and category details",
    "Review practice content for injury, family and defense consultation routes",
    "Resolve site access issues, internal links and relevant service schema",
    "Confirm legal-directory listings and pursue appropriate editorial outreach opportunities",
    "Request genuine client reviews and prepare privacy-conscious public responses",
    "Deliver monthly reports connecting completed work with calls and forms"
  ],
  "practiceHeading": "SEO playbooks for every Chicago practice area.",
  "practiceOrder": [
    "personal-injury-lawyer-seo",
    "family-law-lawyer-seo",
    "criminal-defense-lawyer-seo",
    "dui-dwi-lawyer-seo",
    "estate-planning-lawyer-seo",
    "immigration-lawyer-seo",
    "employment-law-lawyer-seo",
    "corporate-business-lawyer-seo",
    "workers-compensation-lawyer-seo"
  ],
  "focus": [
    {
      "heading": "Personal Injury / Family Law / Criminal Defense SEO in Chicago",
      "text": "Personal injury, family law and criminal defense searches each need a route through the Chicago website that matches how clients actually ask for help, rather than one broad page treating every matter alike. We separate general research questions from details an attorney should review once intake begins. The content brief lists accepted matters, consultation steps and the approvals your attorneys require before anything publishes, and your team confirms scope before the page goes live, keeping claims tied to real capacity.",
      "url": "/practice-areas/personal-injury-lawyer-seo",
      "label": "Personal Injury SEO strategy",
      "additionalLinks": [
        {
          "url": "/practice-areas/family-law-lawyer-seo",
          "label": "Family Law SEO strategy"
        },
        {
          "url": "/practice-areas/criminal-defense-lawyer-seo",
          "label": "Criminal Defense SEO strategy"
        }
      ]
    }
  ],
  "faqs": [
    {
      "q": "What does SEO cost for attorneys in Chicago?",
      "a": "The entry plan starts at $200/month, with scope set by office count, website condition and practice pages needing work. We review those factors before recommending a tier. The proposal lists included tasks so partners can compare them against budget, instead of paying for a promised position."
    },
    {
      "q": "How long can local search progress take in Chicago?",
      "a": "Local pack movement typically takes 4–6 months, and competitive practice areas can need longer. We first check whether profile errors or site issues limit visibility before newer work begins. Reports separate finished repairs from observed ranking changes, so partners judge progress using calls alongside search movement."
    },
    {
      "q": "Which legal practices can you support?",
      "a": "Personal injury, family law and criminal defense are the current focus areas for this market. Your attorneys decide which matters to prioritize based on accepted work and the consultations intake staff can manage. Each practice page should guide readers toward the team handling that case type."
    },
    {
      "q": "How do you approach Google Business Profile and Maps?",
      "a": "Profile review checks the verified address, categories and contact details against the practice that exists. We confirm the website link sends visitors to relevant information, not a generic page. Genuine reviews help readers compare firms, and a wider service area setting does not create another office."
    },
    {
      "q": "Can AI search visibility help a law firm?",
      "a": "Google AI Overviews, ChatGPT and Perplexity may describe local firms using information already published online. We review those descriptions against approved attorney and practice facts, flagging anything inaccurate. Reporting keeps AI mentions separate from calls, consultations and signed cases intake staff can confirm."
    },
    {
      "q": "How is advertising-rule review handled?",
      "a": "Attorneys reviewing Chicago content check it against Illinois Rules of Professional Conduct 7.1–7.3 before publishing. That review confirms accurate descriptions, required disclosures and appropriate language around outcomes. Approved wording stays on record, and unsupported certifications or results are never added to a page."
    },
    {
      "q": "What will a partner see in monthly reporting?",
      "a": "Monthly reports link completed tasks with search observations, incoming calls and submitted forms. Intake records add which contacts became consultations or signed cases where the source is clear. Gaps in attribution stay marked rather than estimated, helping partners decide which practice page needs attention next."
    },
    {
      "q": "How do you select nearby areas for a firm?",
      "a": "Coverage begins with where your office operates and which nearby communities your attorneys can genuinely serve. Evanston, Naperville, Schaumburg and Oak Brook may each involve different consultation arrangements than a visit to your main address. Those arrangements shape the website, not assumed regional reach."
    }
  ],
  "nearbyHeading": "Lawyer & Law Firm SEO Near Chicago",
  "nearbyLinks": [
    {
      "name": "Illinois",
      "url": "/lawyer-seo/illinois-seo"
    }
  ],
  "servicesText": "See how office profile work, site repairs and practice content come together in our",
  "caseDirectoryText": "Browse documented legal markets in our case-study directory to help shape questions about your practice.",
  "finalText": "Request a Chicago audit built around your actual office, accepted practice areas and current intake capacity. Partners can review the findings before choosing which priorities deserve funding next quarter across the firm.",
  "adRules": "Illinois Rules of Professional Conduct 7.1–7.3",
  "trustIntro": "Every Chicago engagement is run by the same senior legal SEO team, under the same exclusivity and reporting standards.",
  "processNote": "Every page is reviewed against Illinois Rules of Professional Conduct 7.1–7.3.",
  "bostonAi": false,
  "source": {
    "Secondary": "attorney seo chicago; seo for lawyers chicago; law firm seo services chicago; attorney seo services chicago; chicago lawyer seo company",
    "Tertiary": "law firm marketing chicago; lawyer seo services chicago; legal seo chicago; law firm seo in chicago",
    "Practice focus": "personal injury seo chicago; seo for family law attorneys in chicago; criminal defense seo chicago"
  }
};
